import { Component, inject, Input, OnInit } from '@angular/core';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizInformationTypes, FieldTypes, IBizEntity } from '@optima-stride-ai/core';
import { IDeleteAPIResponse } from '@optima-stride-ai/optima-app-framework';
import { IListView, IListViewItem, iProcessProgress, ListerComponent, ProcessProgressType, ProcessProgressView, ProgressIndicatorComponent, ShimmerVariant } from '@optima-stride-ai/optima-components';
import { getAvatarEle } from '../../utils/util.service';


@Component({
    selector: 'app-confirmation-dialogue',
    imports: [ListerComponent, ProgressIndicatorComponent],
    templateUrl: './confirmation-dialogue.component.html',
    styleUrl: './confirmation-dialogue.component.sass'
})
export class ConfirmationDialogueComponent implements OnInit{
  @Input() message: string = '';
  @Input() apiMethodCall: Function = () => {};
  @Input() apiMethodCallParams: any[] = [];
  @Input() bizEntity: IBizEntity | string | Array<IBizEntity | string> | undefined;

  readonly benBizEntityStateStoresService = inject(BizEntityStateStoreService);
  apiResponse: IDeleteAPIResponse | undefined;
  primaryMessage = '';
  confirmationMessage = '';
  apiMessage = '';
  hasTxnLogWarning = false;
  entities: IBizEntity[] = [];
  entityNamesListView: IListView<IBizEntity> = {
    title: 'Entities',
    items: [],
    headerActions: []
  };
  isMultiple = false;
  isLoading = true;

  readonly ProcessProgressView = ProcessProgressView;
  readonly ShimmerVariant = ShimmerVariant;
  readonly shimmerBaseColor = 'var(--cui-secondary-bg)';
  readonly shimmerHighlightColor = 'var(--cui-tertiary-bg)';
  readonly loadingProgress: iProcessProgress = { id: 'deletion-loading', type: ProcessProgressType.Indeterminate };

  ngOnInit(): void {
    const { ids, names } = this.extractIdsAndNames(this.bizEntity);
    this.isMultiple = ids.length > 1;

    this.benBizEntityStateStoresService.getBizEntityDeleteStatus(ids).then((response: IDeleteAPIResponse) => {
      if (!response) {
        return;
      }

      const normalizedResponse = this.normalizeDeleteStatusResponse(response);

      this.apiResponse = normalizedResponse;
      this.apiMessage = normalizedResponse.message || '';
      this.hasTxnLogWarning = !!normalizedResponse.hasTxnLog;

      const referenceName = this.resolveReferenceName(names, normalizedResponse);
      const entityList = this.getEntitiesFromResponse(normalizedResponse, this.bizEntity);

      if (normalizedResponse.isDeletable) {
        if (this.isMultiple) {
          this.primaryMessage = 'Multiple entities are being deleted!';
          this.entities = entityList;
          this.prepareEntityNamesListView();
          this.confirmationMessage = 'Are you sure you want to proceed?';
        } else {
          this.primaryMessage = `Are you sure you want to delete ${referenceName}?`;
          this.entities = entityList;
          this.prepareEntityNamesListView();
          this.confirmationMessage = '';
        }
      } else {
        if (this.isMultiple) {
          this.primaryMessage = `${referenceName} cannot be deleted because one or more entities are blocking it!`;
          this.entities = this.getBlockedEntities(normalizedResponse, this.bizEntity);
          this.prepareEntityNamesListView();
        } else {
          this.primaryMessage = `${referenceName} cannot be deleted.`;
          this.entities = this.getBlockedEntities(normalizedResponse, this.bizEntity, referenceName);
          this.prepareEntityNamesListView();
        }
        this.confirmationMessage = '';
      }

      // Keep backwards compatibility for callers expecting `message`.
      this.message = this.primaryMessage;
      this.isLoading = false;
    });
  }

  private normalizeDeleteStatusResponse(response: IDeleteAPIResponse): IDeleteAPIResponse {
    const payload = response as IDeleteAPIResponse & { success?: boolean };
    const isDeletable = typeof payload.isDeletable === 'boolean'
      ? payload.isDeletable
      : !!payload.success;

    return {
      ...payload,
      isDeletable,
      bizEntityList: Array.isArray(payload.bizEntityList) ? payload.bizEntityList : [],
      bizEntityRelationshipList: Array.isArray(payload.bizEntityRelationshipList)
        ? payload.bizEntityRelationshipList
        : []
    };
  }

  private extractIdsAndNames(input: IBizEntity | string | Array<IBizEntity | string> | undefined): { ids: string[]; names: string[] } {
    if (!input) {
      return { ids: [], names: [] };
    }

    const values = Array.isArray(input) ? input : [input];
    const ids = values
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item?.id === undefined || item?.id === null) {
          return '';
        }
        return String(item.id);
      })
      .filter((id) => !!id);

    const names = values
      .map((item) => (typeof item === 'string' ? item : (item?.name || String(item?.id || ''))))
      .filter((name) => !!name);

    return { ids, names };
  }

  private resolveReferenceName(inputNames: string[], response: IDeleteAPIResponse): string {
    if (inputNames.length > 0) {
      return inputNames[0];
    }
    const firstResponseName = response.bizEntityList?.find((entity) => !!entity?.name)?.name;
    if (firstResponseName) {
      return firstResponseName;
    }
    const firstResponseId = response.bizEntityList?.[0]?.id;
    if (firstResponseId !== undefined && firstResponseId !== null) {
      return String(firstResponseId);
    }
    return 'This entity';
  }

  private getEntitiesFromResponse(response: IDeleteAPIResponse, fallbackInput: IBizEntity | string | Array<IBizEntity | string> | undefined): IBizEntity[] {
    const entitiesFromResponse = (response.bizEntityList || []).filter((entity) => !!entity);
    if (entitiesFromResponse.length > 0) {
      return entitiesFromResponse;
    }

    // Fallback: extract entities from input
    if (!fallbackInput) return [];
    const values = Array.isArray(fallbackInput) ? fallbackInput : [fallbackInput];
    return values.filter((item) => typeof item === 'object' && item !== null) as IBizEntity[];
  }

  private getBlockedEntities(response: IDeleteAPIResponse, fallbackInput: IBizEntity | string | Array<IBizEntity | string> | undefined, referenceName?: string): IBizEntity[] {
    const relationshipBlockedEntities = this.getBlockedRelationshipEntities(response, referenceName);
    if (relationshipBlockedEntities.length > 0) {
      return relationshipBlockedEntities;
    }

    const blockedEntities = (response.bizEntityList || [])
      .filter((entity) => (entity as unknown as { isDelete?: number }).isDelete === 2 && !!entity);

    if (blockedEntities.length > 0) {
      return blockedEntities;
    }

    return this.getEntitiesFromResponse(response, fallbackInput);
  }

  private getBlockedRelationshipEntities(response: IDeleteAPIResponse, referenceName?: string): IBizEntity[] {
    const relationships = (response as unknown as {
      bizEntityRelationshipList?: Array<{
        isDelete?: number;
        fromBizEntity?: IBizEntity;
        toBizEntity?: IBizEntity;
      }>;
    }).bizEntityRelationshipList || [];

    const entities = relationships
      .filter((relationship) => relationship?.isDelete === 2)
      .map((relationship) => {
        if (relationship?.fromBizEntity && (!referenceName || relationship.fromBizEntity.name !== referenceName)) {
          return relationship.fromBizEntity;
        }
        return relationship?.toBizEntity || null;
      })
      .filter((entity) => !!entity) as IBizEntity[];

    // Remove duplicates by id
    const seen = new Set<string | number>();
    return entities.filter((entity) => {
      const key = entity.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private prepareEntityNamesListView(): void {
    const items: IListViewItem<IBizEntity>[] = this.entities.map((entity, index) => ({
      id: `entity-${entity.id ?? index}`,
      itemAction: {
        label: 'View',
        icon: 'cilList',
        tooltip: 'Entity Details',
        action: () => {}
      },
      columns: [
        { key: 'icon', label: 'Icon', value: entity.icon, metaData: { name: 'icon', label: 'L1', type: FieldTypes.text }, bizInformationType: BizInformationTypes.icon },
        { key: 'name', label: 'Name', value: entity.name, metaData: { name: 'name', label: 'L2', type: FieldTypes.text }, bizInformationType: BizInformationTypes.text },
        { key: 'type', label: 'Type', value: entity.type?.name, metaData: { name: 'type.name', label: 'L3', type: FieldTypes.text }, bizInformationType: BizInformationTypes.text },
        { key: 'owner', label: 'Owner', value: entity.ownerDetail?.avatar || 'https://ui-avatars.com/api/?name=Optima+Stride&background=0D8ABC&color=fff', metaData: { name: 'ownerDetail.first_name', label: 'L7', type: FieldTypes.text }, optimaHTMLElement: getAvatarEle(), bizInformationType: BizInformationTypes.image },
      ],
      actions: []
    }));

    this.entityNamesListView = {
      title: 'Entities',
      items,
      headerActions: []
    };
  }

  get data(): IDeleteAPIResponse | undefined{
    return this.apiResponse;
  }
}
